import React from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  Avatar,
  Button,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  LocationOn,
  Home,
  PersonOutline,
  Send,
  ChatBubbleOutline,
  ArrowForwardIos,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const BRAND = '#7B3F00';
// Brighter orange accent used by the "How it works" steps (numbers, icons, arrows)
const ACCENT = '#C2710C';
const ACCENT_RING = 'rgba(194, 113, 12, 0.35)';

const howItWorksSteps = [
  {
    step: 1,
    icon: <PersonOutline sx={{ fontSize: { xs: 26, sm: 32 }, color: ACCENT }} />,
    title: 'Find a worker',
    desc: 'Search and find the right worker for your job.',
  },
  {
    step: 2,
    icon: <Send sx={{ fontSize: { xs: 22, sm: 28 }, color: ACCENT }} />,
    title: 'Send request',
    desc: 'Send your job request to interested workers.',
  },
  {
    step: 3,
    icon: <ChatBubbleOutline sx={{ fontSize: { xs: 26, sm: 32 }, color: ACCENT }} />,
    title: 'Chat & hire',
    desc: 'Chat, compare and hire the best fit for your job.',
  },
];

const JobRequestsSection = ({
  jobRequests,
  loadingJobs,
  formatCurrency,
  user,
  isBasicProfileComplete,
}) => {
  const navigate = useNavigate();

  const handleEnquireClick = (jobRequestId) => {
    const enquiryPath = `/job-requests/${jobRequestId}/enquire`;
    if (!user) {
      localStorage.setItem('pendingBookingUrl', enquiryPath);
      const completeProfilePath = `/complete-profile?redirect=${encodeURIComponent(enquiryPath)}`;
      navigate(`/login?redirect=${encodeURIComponent(completeProfilePath)}`);
      return;
    }
    if (!isBasicProfileComplete) {
      navigate(`/complete-profile?redirect=${encodeURIComponent(enquiryPath)}`);
      return;
    }
    navigate(enquiryPath);
  };

  const visibleJobs = Array.isArray(jobRequests) ? jobRequests.slice(0, 3) : [];

  return (
    <Box sx={{ py: { xs: 3, md: 4 }, bgcolor: '#FAF8F5' }}>
      <Container maxWidth="lg">
        {/* Section Header — only shown when there are jobs or while loading */}
        {(loadingJobs || visibleJobs.length > 0) && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2.5,
              gap: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  bgcolor: BRAND,
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Home sx={{ fontSize: 20, color: 'white' }} />
              </Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: '#323232',
                  fontSize: { xs: '1.05rem', sm: '1.25rem', md: '1.45rem' },
                  whiteSpace: 'nowrap',
                }}
              >
                Available Jobs
              </Typography>
            </Box>
            <Button
              variant="contained"
              onClick={() => navigate('/job-requests')}
              sx={{
                bgcolor: BRAND,
                color: 'white',
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 1.5,
                px: { xs: 2, sm: 3 },
                py: 1,
                fontSize: '0.875rem',
                whiteSpace: 'nowrap',
                boxShadow: 'none',
                '&:hover': { bgcolor: '#5D3000', boxShadow: 'none' },
              }}
            >
              View All Jobs
            </Button>
          </Box>
        )}

        {/* Job Cards */}
        {loadingJobs ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress sx={{ color: BRAND }} />
          </Box>
        ) : visibleJobs.length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 4 }}>
            {visibleJobs.map((jobRequest) => (
              <Card
                key={jobRequest._id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: { xs: 1.5, sm: 2 },
                  p: { xs: 1.5, sm: 2, md: 2.5 },
                  borderRadius: 2,
                  boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
                  bgcolor: 'white',
                  border: '1px solid rgba(0,0,0,0.06)',
                  transition: 'box-shadow 0.2s',
                  '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.12)' },
                }}
              >
                {/* Avatar */}
                <Avatar
                  src={jobRequest.posterPhoto || ''}
                  sx={{
                    width: { xs: 50, sm: 58 },
                    height: { xs: 50, sm: 58 },
                    bgcolor: '#f0e6dc',
                    flexShrink: 0,
                  }}
                >
                  <PersonOutline sx={{ color: BRAND, fontSize: 30 }} />
                </Avatar>

                {/* Content */}
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 700, color: '#212121', lineHeight: 1.3, mb: 0.25 }}
                  >
                    {jobRequest.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: '#757575', mb: 0.5, lineHeight: 1.4, fontSize: '0.82rem' }}
                  >
                    {jobRequest.description.length > 70
                      ? `${jobRequest.description.substring(0, 70)}...`
                      : jobRequest.description}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mb: 0.3 }}>
                    <LocationOn sx={{ fontSize: 14, color: '#9e9e9e', flexShrink: 0 }} />
                    <Typography variant="caption" sx={{ color: '#616161' }}>
                      {jobRequest.workLocation.city}, {jobRequest.workLocation.state}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{ color: '#c0392b', fontWeight: 700, fontSize: '0.9rem' }}
                  >
                    {formatCurrency(
                      jobRequest.budget.amount,
                      jobRequest.budget.currency,
                      jobRequest.budget.paymentType
                    )}
                  </Typography>
                </Box>

                {/* Actions */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    gap: 1,
                    flexShrink: 0,
                  }}
                >
                  <Chip
                    label={jobRequest.category}
                    variant="outlined"
                    size="small"
                    sx={{
                      borderColor: '#bdbdbd',
                      color: '#616161',
                      fontSize: '0.75rem',
                      height: 26,
                    }}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => handleEnquireClick(jobRequest._id)}
                    sx={{
                      bgcolor: BRAND,
                      color: 'white',
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: 1.5,
                      px: 2,
                      py: 0.75,
                      fontSize: '0.8rem',
                      boxShadow: 'none',
                      whiteSpace: 'nowrap',
                      '&:hover': { bgcolor: '#5D3000', boxShadow: 'none' },
                    }}
                  >
                    Enquiry Now
                  </Button>
                </Box>
              </Card>
            ))}
          </Box>
        ) : null}

        {/* How it works */}
        <Box sx={{ mt: visibleJobs.length > 0 ? 1 : 2 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              textAlign: 'center',
              color: '#7A4B2A',
              mb: 3,
              fontSize: { xs: '1.1rem', sm: '1.3rem' },
            }}
          >
            How it works
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'center',
              flexWrap: 'nowrap',
              gap: { xs: 0.25, sm: 1 },
            }}
          >
            {howItWorksSteps.map((item, index) => (
              <React.Fragment key={item.step}>
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    maxWidth: { xs: 'none', sm: 220 },
                    textAlign: 'center',
                    px: { xs: 0.25, sm: 1 },
                  }}
                >
                  {/* Icon circle with step number badge on top */}
                  <Box sx={{ position: 'relative', display: 'inline-block', mb: 1.25 }}>
                    <Box
                      sx={{
                        width: { xs: 52, sm: 66 },
                        height: { xs: 52, sm: 66 },
                        borderRadius: '50%',
                        bgcolor: 'white',
                        border: `2px solid ${ACCENT_RING}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -3,
                        left: -3,
                        width: { xs: 19, sm: 22 },
                        height: { xs: 19, sm: 22 },
                        borderRadius: '50%',
                        bgcolor: ACCENT,
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: { xs: '0.62rem', sm: '0.72rem' },
                        fontWeight: 700,
                        border: '2px solid #fff',
                      }}
                    >
                      {item.step}
                    </Box>
                  </Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 700,
                      color: '#212121',
                      mb: 0.5,
                      fontSize: { xs: '0.8rem', sm: '0.95rem' },
                      lineHeight: 1.2,
                    }}
                  >
                    {item.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#757575',
                      lineHeight: 1.4,
                      fontSize: { xs: '0.66rem', sm: '0.8rem' },
                    }}
                  >
                    {item.desc}
                  </Typography>
                </Box>

                {/* Dashed arrow connector between steps (aligned with the circles) */}
                {index < howItWorksSteps.length - 1 && (
                  <Box
                    aria-hidden="true"
                    sx={{
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '3px',
                      color: ACCENT,
                      mt: { xs: '22px', sm: '29px' },
                    }}
                  >
                    <Box
                      sx={{
                        width: { xs: 8, sm: 18 },
                        borderTop: '2px dotted',
                        borderColor: ACCENT,
                        opacity: 0.6,
                      }}
                    />
                    <ArrowForwardIos sx={{ fontSize: { xs: 10, sm: 13 } }} />
                  </Box>
                )}
              </React.Fragment>
            ))}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default React.memo(JobRequestsSection);
